/**
 * Copy-the-payment-link UI for Openhouse At Apartment.
 *
 * WHY THIS EXISTS: the booking / renewal WhatsApp goes out on a template Meta
 * classifies as Marketing, and a Marketing template can be withheld from a
 * parent without any failure being reported back — `whatsapp_sent` still comes
 * back true. So Alfred cannot treat "we sent it" as "they got it". Every link
 * is surfaced for a human to copy and send manually, both at the moment it is
 * created and afterwards from the Booking table.
 *
 * The clipboard write deliberately has NO `await` in front of it: the browser
 * only permits it while the user's click is still being handled, and awaiting
 * anything first is what makes it refuse. Same shape as
 * Operations/UnderageConsent's copyLink. execCommand is the fallback for
 * contexts where the async API is unavailable (non-secure origins).
 */
import { Button, Tooltip, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import React from 'react'

import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

export const copyTextToClipboard = (text, message = 'Payment link copied') => {
  if (!text) return
  const done = () => eventAlert(message, constants.SUCCESS)
  try {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(done)
      return
    }
    const ta = document.createElement('textarea')
    ta.value = text
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    document.execCommand('copy')
    ta.remove()
    done()
  } catch (e) {
    eventAlert(
      'Could not copy automatically — select the link and copy it by hand',
      constants.ERROR
    )
  }
}

const useStyles = makeStyles((theme) => ({
  cell: { display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) },
  // The URL is long and the Booking grid is already wide; clamp it so the
  // column cannot push the table into a second horizontal scroller.
  url: {
    maxWidth: 190,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  panel: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.default
  },
  panelUrl: {
    // Long URLs must wrap here rather than clip — this is the copy of record
    // if the clipboard write fails and someone has to read it off the screen.
    wordBreak: 'break-all',
    fontFamily: 'monospace',
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1.5)
  },
  note: { display: 'block', marginTop: theme.spacing(1) }
}))

/** Table-cell rendering: the link, truncated, with a copy button beside it. */
export const PaymentLinkCell = ({ url, status }) => {
  const classes = useStyles()
  if (!url) return '—'
  // Status rides along because the newest link is shown whatever its state —
  // a coordinator about to paste one needs to see it reads Paid or Expired.
  const stale = status === 'Paid' || status === 'Expired'
  return (
    <span className={classes.cell}>
      <Tooltip title={url}>
        <a
          className={classes.url}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {url}
        </a>
      </Tooltip>
      <Tooltip
        title={
          stale
            ? `Copy link (this one is ${status} — resend for a fresh link)`
            : 'Copy link'
        }
      >
        <Button
          size="small"
          onClick={() => copyTextToClipboard(url)}
          aria-label="Copy payment link"
        >
          <FileCopyOutlinedIcon fontSize="small" />
        </Button>
      </Tooltip>
    </span>
  )
}

/**
 * Shown straight after a link is minted, so it is never lost to a toast that
 * disappears. `whatsappSent === false` is the backend's explicit "the send
 * failed"; `undefined` means an older API that does not report it, which is
 * why the check is against false rather than falsiness.
 */
export const PaymentLinkPanel = ({ url, amountLabel, whatsappSent }) => {
  const classes = useStyles()
  if (!url) return null
  return (
    <div className={classes.panel}>
      <Typography variant="subtitle2">
        {amountLabel ? `Payment link (${amountLabel})` : 'Payment link'}
      </Typography>
      <Typography className={classes.panelUrl} variant="body2">
        {url}
      </Typography>
      <Button
        variant="contained"
        color="primary"
        size="small"
        startIcon={<FileCopyOutlinedIcon fontSize="small" />}
        onClick={() => copyTextToClipboard(url)}
      >
        Copy link
      </Button>
      <Typography
        className={classes.note}
        variant="caption"
        color={whatsappSent === false ? 'error' : 'textSecondary'}
      >
        {whatsappSent === false
          ? 'The WhatsApp did NOT go out. Send this link to the parent yourself.'
          : 'WhatsApp was accepted, but delivery is not guaranteed on this template — send this link to the parent as well.'}
      </Typography>
    </div>
  )
}
